import { Trans, useLingui } from '@lingui/react/macro';
import { Plus } from 'lucide-react';
import { Game } from 'modules/games/domain/interfaces/game';
import { StageType } from 'modules/championship/domain/enums/stage-type';
import { UpsertGameDto } from 'modules/championship/domain/repositories/championship-game.repository';
import { UDText } from 'modules/ud-ui/components';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDTippy from 'ud-ui/components/tippy';
import ChooseGroupForm from 'modules/games/ui/components/choose-group-game-form/choose-group-form';
import { UpsertMatchParams } from 'modules/games/domain/interfaces/upsert-match-params';
import CreateGameForm from 'modules/games/ui/components/create-game-form';
import GamesList from 'modules/games/ui/components/games-list/games-list';
import { StageMatchTab } from 'modules/championship/domain/enums/stage-match-tab';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { fromMatchOverlayState } from 'modules/devices/domain/constants';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { deleteChampionshipGame, upsertChampionshipGame } from 'modules/championship/store/championship/actions';
import { championshipDataSelector } from 'modules/championship/store/championship/selectors';
import { selectSelectedStageGroups, stageGameDetailsListSelector, stageProcessingSelector } from 'modules/championship/store/stages';
import { fetchStages, generateStageMatches } from 'modules/championship/store/stages/stages.actions';
import { fetchGameDetails } from 'modules/games/store/actions';
import { getGenerateMatchesErrorMessage } from './generate-matches-error';

type StageMatchesProps = {
  tournamentId: number;
  championshipId: number;
  stage: StageV2;
};

const StageMatches = (props: StageMatchesProps) => {
  const { stage, tournamentId, championshipId } = props;

  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // Матч, открытый в модалке редактирования. К адресу отношения не имеет:
  // страница матча живёт на своём роуте и этот экран не монтирует.
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const championship = useSelector(championshipDataSelector(championshipId));
  const { isProcessing } = useSelector(stageProcessingSelector);
  const gameDetailsList = useSelector(stageGameDetailsListSelector);

  const groups = useSelector(selectSelectedStageGroups);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const open = useCallback(() => setIsCreateOpen(true), []);
  const close = useCallback(() => setIsCreateOpen(false), []);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const openEdit = useCallback(() => setIsEditOpen(true), []);
  const closeEdit = useCallback(() => setIsEditOpen(false), []);

  const [isChooseGroupOpen, setIsChooseGroupOpen] = useState(false);
  const openChooseGroup = useCallback(() => setIsChooseGroupOpen(true), []);
  const closeChooseGroup = useCallback(() => setIsChooseGroupOpen(false), []);

  const [isGenerateConfirmOpen, setIsGenerateConfirmOpen] = useState(false);

  const AUTOGENERATE_SUPPORTED_TYPES = [StageType.ROUND_ROBIN_1, StageType.ROUND_ROBIN_2, StageType.GROUP];
  const isAutoGenerateSupported = AUTOGENERATE_SUPPORTED_TYPES.includes(stage.type as StageType);

  const { teams } = stage;

  const handleGenerateMatchesConfirm = useCallback(() => {
    setIsGenerateConfirmOpen(false);
    dispatch(generateStageMatches(stage.id))
      .unwrap()
      .then(() => {
        toast.success(t`Матчи успешно сгенерированы`);
      })
      .catch((error) => {
        toast.error(t(getGenerateMatchesErrorMessage(error)), {
          autoClose: false,
        });
      });
  }, [dispatch, stage.id, t]);

  const handleCreateGameClick = useCallback(() => {
    if (stage.type === 'GROUP') {
      openChooseGroup();
    } else {
      open();
    }
  }, [open, openChooseGroup, stage.type]);

  const handleEditGameClick = useCallback(
    (game: Game) => {
      if (!gameDetailsList?.byId[game.id]) {
        dispatch(fetchGameDetails({ gameId: game.id, championshipId }));
      }

      setEditingGame(game);
      openEdit();
    },
    [dispatch, openEdit, gameDetailsList, championshipId],
  );

  const handleCloseEditMatchDialog = useCallback(() => {
    setEditingGame(null);
    closeEdit();
  }, [closeEdit]);

  const openMatchPage = useCallback(
    (gameId: number, tab: StageMatchTab) => {
      navigate(Pages.CHAMPIONSHIPS.STEPS.builders.match(championshipId, stage.id, gameId, tab));
    },
    [navigate, championshipId, stage.id],
  );

  // Ссылка на табло больше не строится по матчу — она принадлежит устройству,
  // поэтому кнопка ведёт на «Устройства», где эту ссылку и копируют.
  const handleShowOverlayLinks = useCallback(() => {
    navigate(Pages.DEVICES.INDEX, { state: fromMatchOverlayState });
  }, [navigate]);

  const handleDeleteGameClick = useCallback(
    (gameId: number) => {
      dispatch(deleteChampionshipGame({ gameId, championshipId }))
        .then((result) => {
          if (result.payload) {
            toast.success(t`Матч удален`);
            dispatch(fetchStages(championshipId));
          } else {
            toast.error(t`Ошибка удаления матча`);
          }
        })
        .catch(() => {
          toast.error(t`Ошибка удаления матча`);
        });
    },
    [dispatch, championshipId, t],
  );

  const handleCreateGameSubmit = useCallback(
    async (values: UpsertMatchParams) => {
      const params = {
        tournamentId,
        championshipId,
        values,
      };

      dispatch(upsertChampionshipGame(params))
        .then((result) => {
          if (result.payload) {
            close();
          } else {
            toast.error(t`Ошибка создания матча`);
          }
        })
        .catch((err) => {
          console.error('create game error:', err);
          toast.error(t`Ошибка создания матча`);
        });
    },
    [tournamentId, championshipId, close, dispatch, t],
  );

  const handleEditGameSubmit = useCallback(
    async (values: UpsertMatchParams, setSubmitting: (isSubmitting: boolean) => void) => {
      if (!editingGame) {
        return;
      }

      const gameDetails = gameDetailsList?.byId[editingGame.id];

      const params: UpsertGameDto = {
        tournamentId,
        championshipId,
        matchId: editingGame.id,
        values: {
          ...values,
          ...(gameDetails &&
            editingGame.hasResults && {
              // ...(gameDetails && {
              events: gameDetails?.events,
              result: gameDetails?.result,
              videoUrls: gameDetails?.videoUrls,
            }),
        },
      };

      dispatch(upsertChampionshipGame(params))
        .then((result) => {
          if (result.payload) {
            closeEdit();
          } else {
            toast.error(t`Ошибка редактирования матча`);
          }
        })
        .catch((err) => {
          console.error('update game error:', err);
          toast.error(t`Ошибка редактирования матча`);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [editingGame, gameDetailsList?.byId, tournamentId, championshipId, dispatch, closeEdit, t],
  );

  const teamsForCreate = useMemo(() => {
    if (selectedGroupId) {
      return groups.find((group) => group.id === selectedGroupId)?.teams || [];
    }

    return teams;
  }, [groups, selectedGroupId, teams]);

  const handleChooseGroup = useCallback(
    (id: number) => {
      setSelectedGroupId(id);
      closeChooseGroup();
      open();
    },
    [closeChooseGroup, open],
  );

  return (
    <div className="row gap-y-6">
      <div className="w-full">
        <div className={'border-DarkBlue/20 flex justify-between rounded-lg border bg-white py-2'} style={{ paddingInline: 14 }}>
          <div className={'flex-center'}>
            <UDText variant={'body-1'} className={'text-nowrap'} center>
              <Trans>Добавить новые матчи</Trans>
            </UDText>
          </div>
          <div className="flex w-full justify-end" style={{ gap: '8px' }}>
            <UDTippy
              content={!isAutoGenerateSupported ? t`Данный тип этапа не поддерживает автогенерацию матчей` : undefined}
              placement="top"
            >
              <span style={{ display: 'inline-flex' }}>
                <UDButton
                  variant="primary"
                  className="flex items-center"
                  onClick={() => setIsGenerateConfirmOpen(true)}
                  loading={isProcessing}
                  disabled={!isAutoGenerateSupported}
                >
                  <Plus size={16} className="mr-2" />
                  <Trans>Автозаполнение</Trans>
                </UDButton>
              </span>
            </UDTippy>
            <UDButton variant="dark" className="ml-6 flex items-center" onClick={handleCreateGameClick}>
              {/* <UDIcon name="plus" componentProps={{ className: 'mr-2' }} /> */}
              <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
              <Trans>Добавить матч</Trans>
            </UDButton>
          </div>
        </div>
      </div>
      <div className="w-full">
        <div className="row">
          <div className="w-full">
            <GamesList
              championshipId={championshipId}
              games={stage.matches}
              activeGameId={editingGame?.id}
              onEditGameClick={handleEditGameClick}
              onDeleteGameClick={handleDeleteGameClick}
              onShowGameResultClick={(gameId) => openMatchPage(gameId, StageMatchTab.RESULT)}
              onShowGameTemplatesClick={handleShowOverlayLinks}
              onShowMomentsClick={(gameId) => openMatchPage(gameId, StageMatchTab.MOMENTS)}
            />
          </div>
        </div>
      </div>
      <div className="w-1/2"></div>

      <Modal.Confirm
        show={isGenerateConfirmOpen}
        title={t`Сгенерировать матчи?`}
        description={t`Автозаполнение перезапишет список матчей в зависимости от типа этапа и выбранных команд.`}
        confirmText={t`Сгенерировать`}
        onConfirm={handleGenerateMatchesConfirm}
        onCancel={() => setIsGenerateConfirmOpen(false)}
      />
      <Modal show={isChooseGroupOpen} onClose={closeChooseGroup} size="sm">
        <Modal.Header title={t`Выбор группы`} />
        <Modal.Body>
          <ChooseGroupForm groups={groups} onClick={handleChooseGroup} />
        </Modal.Body>
      </Modal>
      <Modal show={isCreateOpen} onClose={close} size="md">
        <Modal.Header title={t`Добавление матча`} />
        <Modal.Body>
          <CreateGameForm
            stageId={stage.id}
            rounds={stage.rounds}
            teams={teamsForCreate}
            requiredRound={true}
            onSubmit={handleCreateGameSubmit}
            availableLocations={championship?.locations || []}
            championshipId={championshipId}
          />
        </Modal.Body>
      </Modal>
      <Modal show={isEditOpen} onClose={handleCloseEditMatchDialog} size="md">
        <Modal.Header title={t`Редактирование матча`} />
        <Modal.Body>
          <CreateGameForm
            stageId={stage.id}
            rounds={stage.rounds}
            teams={teams}
            game={editingGame!}
            requiredRound={true}
            onSubmit={handleEditGameSubmit}
            availableLocations={championship?.locations || []}
            isLoading={isProcessing}
            championshipId={championshipId}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StageMatches;
